/**
 * pantryMatcher.js
 *
 * Scores recipes against a user's pantry using:
 *  - Exact ingredient name matching (normalized)
 *  - Substitution graph for partial matches
 *  - Dietary filter enforcement
 */

/** Normalize an ingredient string for comparison. */
function normalize(str = '') {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')  // strip punctuation
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extract the core ingredient name from a recipe ingredient string.
 * e.g. "2 Tbsp infused olive oil (from 2 g decarbed flower)" → "infused olive oil"
 */
function extractIngredientName(raw = '') {
  // Strip leading quantity + unit patterns like "2 Tbsp", "1/2 cup", "3 g"
  return raw
    .replace(/^[\d/\s¼½¾⅓⅔⅛⅜⅝⅞]+\s*(tbsp|tsp|cup|oz|g|lb|ml|l|piece|clove|slice|bunch|pinch|handful|pkg|package|can|jar|dash|sprig|stalk|head|bunch)s?\s*/i, '')
    .replace(/\(.*?\)/g, '')  // remove parenthetical notes
    .trim();
}

/**
 * Find a substitution for a missing ingredient from the pantry.
 * Returns quality_score (0–1) or 0 if no sub found.
 */
function findSubstitutionScore(ingredientName, pantrySet, substitutions) {
  const norm = normalize(ingredientName);
  for (const sub of substitutions) {
    if (normalize(sub.ingredient_from) === norm) {
      if (pantrySet.has(normalize(sub.ingredient_to))) {
        return sub.quality_score ?? 0.8;
      }
    }
  }
  return 0;
}

/**
 * Score a single recipe against the user's pantry.
 *
 * @param {object}   recipe        - Recipe object (ingredients is string[])
 * @param {string[]} pantryItems   - Normalized ingredient names the user has
 * @param {object[]} substitutions - [{ingredient_from, ingredient_to, quality_score}]
 * @param {string[]} dietaryFilters - Active filters e.g. ['vegan', 'gluten-free']
 * @returns {object|null} match result, or null if dietary filter violated
 */
export function scoreRecipe(recipe, pantryItems, substitutions = [], dietaryFilters = []) {
  // Dietary hard-block
  if (dietaryFilters.length > 0 && recipe.dietary_flags) {
    const hasViolation = dietaryFilters.some(
      filter => !recipe.dietary_flags.includes(filter)
    );
    if (hasViolation) return null;
  }

  const pantrySet = new Set(pantryItems.map(normalize));
  const ingredients = (recipe.ingredients || []);

  const matched      = [];
  const missing      = [];
  const substituted  = [];
  let   weightedScore = 0;

  for (const raw of ingredients) {
    const name = extractIngredientName(raw);
    const normName = normalize(name);

    if (pantrySet.has(normName)) {
      matched.push(name);
      weightedScore += 1;
    } else {
      // Try broader partial match (user has "olive oil", recipe says "infused olive oil")
      const partialMatch = [...pantrySet].some(p => normName.includes(p) || p.includes(normName));
      if (partialMatch) {
        matched.push(name);
        weightedScore += 0.9;
      } else {
        const subScore = findSubstitutionScore(name, pantrySet, substitutions);
        if (subScore > 0) {
          substituted.push(name);
          weightedScore += subScore;
        } else {
          missing.push(name);
        }
      }
    }
  }

  const total = ingredients.length || 1;
  const matchScore = Math.round((weightedScore / total) * 100);

  return {
    recipe,
    matchScore,
    matched,
    missing,
    substituted,
    canMake: missing.length === 0,  // true if all required ingredients are available
  };
}

/**
 * Score all recipes and return sorted results (highest match first).
 * Filters out recipes with matchScore = 0 unless includeAll is true.
 */
export function matchRecipes({
  recipes,
  pantryItems,
  substitutions = [],
  dietaryFilters = [],
  minScore = 30,
}) {
  if (!pantryItems.length) return [];

  return recipes
    .map(recipe => scoreRecipe(recipe, pantryItems, substitutions, dietaryFilters))
    .filter(result => result !== null && result.matchScore >= minScore)
    .sort((a, b) => b.matchScore - a.matchScore);
}
